/**
 * Tabcordion
 * ----------
 * Accordion on mobile (< 1024px), Tabbed switcher on desktop (>= 1024px).
 * Multiple instances supported. Uses GSAP for animation.
 *
 * Dependencies: GSAP (gsap core only — no plugins required)
 */

const TabbedSwitcher = () => {

  const BREAKPOINT = 1024;

  // --------------------------------------------------------------------------
  // Utility: get current mode
  // --------------------------------------------------------------------------
  function isDesktop() {
    return window.innerWidth >= BREAKPOINT;
  }

  // --------------------------------------------------------------------------
  // Single Tabcordion instance
  // --------------------------------------------------------------------------
  function Tabcordion(root) {
    this.root     = root;
    this.tabs     = Array.from(root.querySelectorAll('.tabcordion__tab'));
    this.panels   = Array.from(root.querySelectorAll('.tabcordion__panel'));
    this.triggers = Array.from(root.querySelectorAll('.tabcordion__trigger'));

    this._mode = null;          // 'accordion' | 'tabs'
    this._animating = false;    // tab crossfade lock

    this._bindEvents();
    this._onResize();           // initial setup
  }

  // --------------------------------------------------------------------------
  // Bind events
  // --------------------------------------------------------------------------
  Tabcordion.prototype._bindEvents = function () {
    var self = this;

    // Tab clicks (desktop)
    this.tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var target = tab.getAttribute('data-tab');
        self._switchTab(target, tab);
      });
    });

    // Accordion trigger clicks (mobile)
    this.triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var panel = trigger.closest('.tabcordion__panel');
        self._toggleAccordion(panel);
      });
    });
  };

  // --------------------------------------------------------------------------
  // Resize handler — switches mode when crossing the breakpoint
  // --------------------------------------------------------------------------
  Tabcordion.prototype._onResize = function () {
    var newMode = isDesktop() ? 'tabs' : 'accordion';

    if (newMode === this._mode) return; // no change, bail early
    this._mode = newMode;

    if (newMode === 'tabs') {
      this._initTabs();
    } else {
      this._initAccordion();
    }
  };

  // --------------------------------------------------------------------------
  // TABS — initialise desktop state
  // --------------------------------------------------------------------------
  Tabcordion.prototype._initTabs = function () {
    var self = this;

    // Kill any lingering inline heights from accordion (CSS handles it via
    // height:auto !important on desktop, but clear just in case)
    this.panels.forEach(function (panel) {
      var body = panel.querySelector('.tabcordion__body');
      gsap.set(body, { clearProps: 'height' });

      // Reset accordion open state
      panel.classList.remove('is-open');
      panel.querySelector('.tabcordion__trigger').setAttribute('aria-expanded', 'false');
    });

    // Check if any tab is already active (e.g. after a resize back to desktop)
    var alreadyActive = this.tabs.find(function (t) {
      return t.classList.contains('is-active');
    });

    if (!alreadyActive) {
      // Activate first tab silently (no animation)
      this._activateTab(this.tabs[0], true);
    } else {
      // Make sure panels match the active tab without animating
      var activeTabId = alreadyActive.getAttribute('data-tab');
      this.panels.forEach(function (panel) {
        if (panel.getAttribute('data-panel') === activeTabId) {
          panel.classList.add('is-active');
          gsap.set(panel, { opacity: 1, visibility: 'visible' });
        } else {
          panel.classList.remove('is-active');
          gsap.set(panel, { opacity: 0, visibility: 'hidden' });
        }
      });
    }
  };

  // --------------------------------------------------------------------------
  // ACCORDION — initialise mobile state
  // --------------------------------------------------------------------------
  Tabcordion.prototype._initAccordion = function () {
    var self = this;

    // Clear any tab-mode classes/styles
    this.tabs.forEach(function (tab) {
      tab.classList.remove('is-active');
    });

    this.panels.forEach(function (panel, index) {
      var body = panel.querySelector('.tabcordion__body');
      var trigger = panel.querySelector('.tabcordion__trigger');

      // Clear desktop gsap overrides
      gsap.set(panel, { clearProps: 'opacity,visibility' });
      panel.classList.remove('is-active');

      if (index === 0) {
        // First panel: open by default
        panel.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        gsap.set(body, { height: 'auto' });
      } else {
        panel.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        gsap.set(body, { height: 0 });
      }
    });
  };

  // --------------------------------------------------------------------------
  // ACCORDION — toggle a panel open/closed
  // --------------------------------------------------------------------------
  Tabcordion.prototype._toggleAccordion = function (panel) {
    var body    = panel.querySelector('.tabcordion__body');
    var trigger = panel.querySelector('.tabcordion__trigger');
    var isOpen  = panel.classList.contains('is-open');

    if (isOpen) {
      // Collapse
      panel.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');

      gsap.to(body, {
        height: 0,
        duration: 0.4,
        ease: 'power2.inOut'
      });
    } else {
      // Expand — animate from 0 to auto height
      panel.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');

      gsap.fromTo(body,
        { height: 0 },
        {
          height: 'auto',
          duration: 0.4,
          ease: 'power2.inOut'
        }
      );
    }
  };

  // --------------------------------------------------------------------------
  // TABS — switch to a new tab with crossfade
  // --------------------------------------------------------------------------
  Tabcordion.prototype._switchTab = function (targetId, clickedTab) {
    if (this._animating) return;

    // Already active? Do nothing.
    if (clickedTab.classList.contains('is-active')) return;

    var self        = this;
    var activePanel = this.panels.find(function (p) {
      return p.classList.contains('is-active');
    });
    var targetPanel = this.panels.find(function (p) {
      return p.getAttribute('data-panel') === targetId;
    });

    if (!targetPanel) return;

    this._activateTab(clickedTab, false, activePanel, targetPanel);
  };

  // --------------------------------------------------------------------------
  // TABS — activate a tab (with or without animation)
  // --------------------------------------------------------------------------
  Tabcordion.prototype._activateTab = function (newTab, instant, outPanel, inPanel) {
    var self = this;

    // Update tab classes
    this.tabs.forEach(function (t) { t.classList.remove('is-active'); });
    newTab.classList.add('is-active');

    if (instant || !outPanel) {
      // No animation — just show the target
      this.panels.forEach(function (p) {
        p.classList.remove('is-active');
        gsap.set(p, { opacity: 0, visibility: 'hidden' });
      });

      if (inPanel) {
        inPanel.classList.add('is-active');
        gsap.set(inPanel, { opacity: 1, visibility: 'visible' });
      } else {
        // Fallback: show first panel
        var first = this.panels[0];
        first.classList.add('is-active');
        gsap.set(first, { opacity: 1, visibility: 'visible' });
      }
      return;
    }

    // Crossfade
    this._animating = true;

    // Prep incoming panel: position it, make it invisible but in DOM flow
    inPanel.classList.add('is-active');
    gsap.set(inPanel, { opacity: 0, visibility: 'visible' });

    // Fade out current, fade in new
    var tl = gsap.timeline({
      onComplete: function () {
        outPanel.classList.remove('is-active');
        gsap.set(outPanel, { opacity: 0, visibility: 'hidden' });
        self._animating = false;
      }
    });

    tl.to(outPanel, { opacity: 0, duration: 0.25, ease: 'power1.out' }, 0)
      .to(inPanel,  { opacity: 1, duration: 0.3,  ease: 'power1.in'  }, 0.1);
  };

  // --------------------------------------------------------------------------
  // Init all instances + shared resize observer
  // --------------------------------------------------------------------------
  function init() {
    var roots    = Array.from(document.querySelectorAll('.tabcordion'));
    var instances = roots.map(function (root) { return new Tabcordion(root); });

    if (!instances.length) return;

    // Single ResizeObserver watching the body — cheaper than window resize
    // for detecting breakpoint crossings
    var lastWidth = window.innerWidth;

    var ro = new ResizeObserver(function () {
      var currentWidth = window.innerWidth;
      if (currentWidth === lastWidth) return; // height-only resize, skip
      lastWidth = currentWidth;

      instances.forEach(function (instance) {
        instance._onResize();
      });
    });

    ro.observe(document.body);
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

}

export {
	TabbedSwitcher
}